import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from "motion/react";
import { useClubData, useInfiniteGalleryData } from '@/features/clubs/hooks';
import { PremiumLoader } from '@/components';
import GalleryHeader from '@/features/clubs/components/GalleryHeader';
import GalleryGrid from '@/features/clubs/components/GalleryGrid';
import GalleryModal from '@/features/clubs/components/GalleryModal';
import type { GalleryItemResponse } from '@/features/clubs/types/types';

// Catchy gallery titles
const GALLERY_TITLES = [
  "Picture Yourself Here",
  "This Could Be You",
  "Where Legends Are Made",
  "Your Next Memory",
  "Step Into The Scene",
  "This Is Where It Happens",
  "Your Night Starts Here",
  "Welcome To The Experience",
  "This Is Your Moment",
  "Where Dreams Come Alive",
  "Your Story Begins Here",
  "This Is The Place",
  "Where Magic Happens",
  "Your Night Awaits",
  "This Is Where You Belong",
  "Where The Night Comes Alive",
  "Your Adventure Starts Now",
  "This Is The Vibe",
  "Where You'll Want To Be",
  "Your Night, Your Way",
  "This Is The Energy",
  "Where The Party Never Ends",
  "Your Night, Our Stage",
  "This Is Where You'll Shine",
  "Where Every Night Is Epic",
  "Your Night, Our World",
  "This Is The Real Deal",
  "Where The Night Gets Real",
  "Your Night, Our Legacy",
  "This Is Where It's At"
];

const ClubGalleryPage: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const [selectedItem, setSelectedItem] = useState<GalleryItemResponse | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch club data for header info
  const { club, loading: clubLoading, error: clubError } = useClubData(handle || '');
  
  // Fetch gallery data using handle with infinite scrolling
  const { 
    galleryItems, 
    loading: galleryLoading, 
    error: galleryError,
    hasMore,
    loadMore,
    loadingMore
  } = useInfiniteGalleryData(handle || '');

  // Random gallery title selection
  const randomTitle = useMemo(() => {
    return GALLERY_TITLES[Math.floor(Math.random() * GALLERY_TITLES.length)];
  }, []); // Empty dependency array ensures it only runs once per page load

  const handleItemClick = (item: GalleryItemResponse, index: number) => {
    setSelectedItem(item);
    setSelectedIndex(index);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleNavigate = (index: number) => {
    setSelectedItem(galleryItems[index]);
    setSelectedIndex(index);
  };

  // Loading state
  if (clubLoading || galleryLoading) {
    return (
      <div className="bg-black min-h-screen">
        <PremiumLoader loading={true} />
      </div>
    );
  }

  // Error states
  if (clubError || !club) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Venue Not Found</h1>
          <p className="text-gray-400 mb-6">
            {clubError || 'The venue you are looking for does not exist.'}
          </p>
          <a 
            href="/venues" 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Venues
          </a>
        </div>
      </div>
    );
  }

  if (galleryError) {
    return (
      <div className="bg-black text-white min-h-screen">
        <GalleryHeader clubName={club.name} clubHandle={handle || ''} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-4">Gallery Unavailable</h2>
            <p className="text-gray-400 mb-6">
              {galleryError}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <GalleryHeader clubName={club.name} clubHandle={handle || ''} />
      
      {/* Hero Section */}
      <motion.section 
        className="relative py-20 px-6 text-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-pink-600/20" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Animated Title */}
          <motion.h1 
            className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent leading-tight"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {randomTitle}
          </motion.h1>
          
          <motion.div
            className="text-xl md:text-2xl text-gray-300 mb-8"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <span className="text-white font-semibold">{club.name}</span>
            <span className="mx-2">•</span>
            <span>Visual Stories & Moments</span>
          </motion.div>

          {/* Animated Description */}
          <motion.p 
            className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Explore the vibrant atmosphere, unforgettable events, and talented artists that make {club.name} a legendary destination.
          </motion.p>
        </div>
      </motion.section>
      
      <motion.main
        className="pt-8 pb-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          duration: 0.8, 
          ease: "easeOut", 
          delay: 0.4,
          type: "spring",
          stiffness: 100,
          damping: 20
        }}
      >
        <GalleryGrid 
          galleryItems={galleryItems} 
          onItemClick={handleItemClick}
          loadMore={loadMore}
          hasMore={hasMore}
          loadingMore={loadingMore}
        />
      </motion.main>

      {/* Gallery Modal */}
      <GalleryModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        currentItem={selectedItem}
        currentIndex={selectedIndex}
        allItems={galleryItems}
        clubHandle={handle || ''}
        onNavigate={handleNavigate}
      />
    </div>
  );
};

export default ClubGalleryPage;
